import { Image } from '@vivaldis/antd-ui'
import { endOfDay, isBefore, isValid, parseISO, startOfDay } from 'date-fns'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { EmployeeProfileQuery_employee } from '../../../../typings/EmployeeProfileQuery'
import { Information } from '../../../../../Information'
import { MissingInformationTag } from '../../../MissingInformationTag'
import { ExpiredInformationTag } from '../../../ExpiredInformationTag'
import { NotSetImageValue } from '../../../NotSetImageValue'

interface Props {
  employee: EmployeeProfileQuery_employee
}

export const IdCard: FC<Props> = ({ employee }) => {
  const [t] = useTranslation('web_ui')

  const { profile } = employee

  // is true when null or undefined to show a short form (only id card fields)
  const eeaMember = profile?.nationalityCountry?.eeaMember ?? false

  // idCard
  const idCardBackPicture = profile?.idCardBackPicture
  const idCardExpiresOn = profile?.idCardExpiresOn
  const idCardFrontPicture = profile?.idCardFrontPicture
  const idCardNumber = profile?.idCardNumber

  const isExpiredIdCardExpiresOnInformation = useMemo(() => {
    if (!idCardExpiresOn) {
      return false
    }

    const expiresAtDate = parseISO(idCardExpiresOn)
    if (!isValid(expiresAtDate)) {
      return true
    }

    return isBefore(endOfDay(expiresAtDate), startOfDay(new Date()))
  }, [idCardExpiresOn])

  if (!eeaMember) {
    return null
  }

  return (
    <>
      {idCardBackPicture || idCardFrontPicture ? (
        <Information.InformationFieldWithTwoImages
          leftLabel={t('employee_profile.id_card_front_picture')}
          rightLabel={t('employee_profile.id_card_back_picture')}
          leftContent={
            <>
              {idCardFrontPicture ? (
                <Image
                  width={238}
                  height={160}
                  src={String(idCardFrontPicture)}
                />
              ) : (
                <div>
                  <div>
                    <MissingInformationTag />
                  </div>
                  <NotSetImageValue />
                </div>
              )}
            </>
          }
          rightContent={
            <>
              {idCardBackPicture ? (
                <Image
                  width={238}
                  height={160}
                  src={String(idCardBackPicture)}
                />
              ) : (
                <div>
                  <div>
                    <MissingInformationTag />
                  </div>
                  <NotSetImageValue />
                </div>
              )}
            </>
          }
        />
      ) : null}
      <Information.InformationField
        label={t('employee_profile.id_card_number')}
      >
        {idCardNumber ?? <MissingInformationTag />}
      </Information.InformationField>
      <Information.InformationField
        label={t('employee_profile.id_card_expires_on')}
      >
        {idCardExpiresOn ? (
          isExpiredIdCardExpiresOnInformation ? (
            <ExpiredInformationTag />
          ) : (
            idCardExpiresOn
          )
        ) : (
          <MissingInformationTag />
        )}
      </Information.InformationField>
    </>
  )
}
